import type { ClientEntry } from "../types";

// TEMPORARY translation-migration tool (client side). Any app or package render
// path can call recordPhraseUsageClient(key, value); the host app wires the
// actual flush (a server action) once via configurePhraseUsage. A page renders
// hundreds of strings, so this NEVER flushes per key: it dedupes against a
// localStorage-backed set (once per key and locale, per browser, across reloads) and
// coalesces new keys into a single batched call once the page is idle.
// Delete this package after the migration.

type FlushFn = (entries: ClientEntry[]) => void | Promise<void>;

export type PhraseUsageClientConfig = {
  flush: FlushFn;
  // Locale the page renders in. Tagged onto every entry and part of the dedupe
  // id, so the same key is recorded once per locale. Update it (by calling
  // configurePhraseUsage again) when the language changes.
  locale?: string;
  storageKey?: string;
  idleTimeoutMs?: number;
  maxBuffer?: number;
};

let flushFn: FlushFn | null = null;
let locale: string | undefined;
let storageKey = "phraseUsage.sent.v1";
let idleTimeoutMs = 3000;
let maxBuffer = 200;

const seen = new Set<string>();
const buffered = new Set<string>();
let pending: ClientEntry[] = [];
let hydrated = false;
let flushScheduled = false;
let listenersBound = false;

const isBrowser = () => typeof window !== "undefined";

const dedupeId = (entry: ClientEntry) =>
  entry.locale ? `${entry.locale}::${entry.key}` : entry.key;

const readStorage = (): string[] => {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const persist = () => {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify([...seen]));
  } catch {
    // Storage unavailable (private mode, quota) — degrade to in-memory only.
  }
};

const hydrate = () => {
  if (hydrated) {
    return;
  }
  hydrated = true;
  for (const key of readStorage()) {
    seen.add(key);
  }
};

const flush = () => {
  flushScheduled = false;
  if (pending.length === 0 || !flushFn) {
    return;
  }
  const batch = pending;
  pending = [];
  Promise.resolve(flushFn(batch))
    .then(() => {
      for (const entry of batch) {
        seen.add(dedupeId(entry));
        buffered.delete(dedupeId(entry));
      }
      persist();
    })
    .catch(() => {
      // Failed send — leave keys unpersisted so they retry on a later visit.
      for (const entry of batch) {
        buffered.delete(dedupeId(entry));
      }
    });
};

const scheduleFlush = () => {
  if (flushScheduled) {
    return;
  }
  flushScheduled = true;
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(flush, { timeout: idleTimeoutMs });
    return;
  }
  window.setTimeout(flush, idleTimeoutMs);
};

const bindLifecycleListeners = () => {
  if (listenersBound) {
    return;
  }
  listenersBound = true;
  const flushOnHide = () => {
    if (document.visibilityState === "hidden") {
      flush();
    }
  };
  document.addEventListener("visibilitychange", flushOnHide);
  window.addEventListener("pagehide", flush);
};

// Called once by the host app to enable recording and provide the flush target
// (typically a "use server" action that resolves brand/locale and writes to the
// CMS). Until this runs, recordPhraseUsageClient is a no-op.
export const configurePhraseUsage = (config: PhraseUsageClientConfig): void => {
  flushFn = config.flush;
  locale = config.locale;
  if (config.storageKey !== undefined) {
    storageKey = config.storageKey;
  }
  if (config.idleTimeoutMs !== undefined) {
    idleTimeoutMs = config.idleTimeoutMs;
  }
  if (config.maxBuffer !== undefined) {
    maxBuffer = config.maxBuffer;
  }
};

// Only keys that resolved to a translation are recorded. No value, or the key
// echoed back as its own fallback, usually means already-translated text passed
// through a translator again (e.g. a validation message); there is nothing to
// import for it anyway.
export const recordPhraseUsageClient = (key: string, value?: string): void => {
  if (!key || !value || value === key || !isBrowser() || !flushFn) {
    return;
  }
  hydrate();
  bindLifecycleListeners();

  const entry: ClientEntry = { key, value, locale };
  const id = dedupeId(entry);
  if (seen.has(id) || buffered.has(id)) {
    return;
  }
  buffered.add(id);
  pending.push(entry);

  if (pending.length >= maxBuffer) {
    flush();
    return;
  }
  scheduleFlush();
};

export default recordPhraseUsageClient;
